import qs from 'qs';
import Endpoint from '../../api/Endpoint';
import mockActivation from '../data/activation';
import mockSubscribers from '../data/notificationSubscribers';
import mockDataExtension from '../data/dataExtension';
import mockEinsteinRecommendations from '../data/einsteinRecommendations';
import mockFrequencies from '../data/frequencies';
import * as Constants from '../constants/';

export function requestActivationStatus() {
  return {
    type: Constants.REQUEST_ACTIVATION_STATUS
  };
}

export function receiveActivationStatus(json, error) {
  return {
    type: Constants.RECEIVE_ACTIVATION_STATUS,
    activationStatus: json,
    error
  };
}

export function getActivationStatus(options = {}) {
  const useMockData = options.useMockData === 'true';
  const path = '/api/v2/activations';
  return new Endpoint(path, requestActivationStatus, receiveActivationStatus)
    .withMock(useMockData ? mockActivation(options.status) : null)
    .withOptions(options)
    .call();
}

export function requestActivation() {
  return {
    type: Constants.REQUEST_ACTIVATION
  };
}

export function receiveActivation(json, error) {
  return {
    type: Constants.RECEIVE_ACTIVATION,
    activationStatus: json,
    error
  };
}

export function submitActivation(options = {}) {
  const useMockActivation = options.useMockActivation === 'true';
  const path = '/api/v2/activations';
  return new Endpoint(path, requestActivation, receiveActivation)
    .withMock(useMockActivation ? mockActivation(options.status) : null)
    .withOptions(options)
    .withMethod('POST')
    .call();
}

export function requestDataExtension() {
  return {
    type: Constants.REQUEST_DATA_EXTENSION
  };
}

export function receiveDataExtension(json, error) {
  return {
    type: Constants.RECEIVE_DATA_EXTENSION,
    dataExtension: json,
    error
  };
}

export function getDataExtension(options = {}) {
  const useMockData = options.useMockData === 'true';
  const useNoDE = options.useNoDE === 'true';
  const path = useNoDE
    ? '/api/v2/foo-bar-data-extensions' // if flag exists, use fake endpoint to generate 404
    : '/api/v2/data-extensions'; // otherwise use normal DE endpoint
  return new Endpoint(path, requestDataExtension, receiveDataExtension)
    .withMock(useMockData ? mockDataExtension : null)
    .withOptions(options)
    .call();
}

export function updateFrequencyRecs(recs) {
  return {
    type: Constants.UPDATE_FREQUENCY_RECS,
    einsteinFrequencyRecs: recs
  };
}

export function requestEinsteinRecommendations() {
  return {
    type: Constants.REQUEST_EINSTEIN_RECOMMENDATIONS
  };
}

export function receiveEinsteinRecommendations(json, error) {
  return {
    type: Constants.RECEIVE_EINSTEIN_RECOMMENDATIONS,
    einsteinDefaults: json,
    error
  };
}

export function getEinsteinRecommendations(options = {}, data = {}) {
  const filter = {
    filter: [
      'lookback',
      'emailType',
      'openRateWeight',
      'clickRateWeight',
      'unsubscribePenaltyFactor',
      'rangeStart',
      'rangeEnd'
    ]
  };
  const queryString = `?${qs.stringify(data, filter)}`;
  const path = `/api/v2/optimal-frequency-range${queryString}`;
  const useMockData = options.useMockData === 'true';
  const useEinsteinDefaults = options.useEinsteinDefaults === 'true';
  return new Endpoint(
    path,
    requestEinsteinRecommendations,
    receiveEinsteinRecommendations
  )
    .withMock(
      useMockData || useEinsteinDefaults
        ? mockEinsteinRecommendations(options.status)
        : null
    )
    .withOptions(options)
    .call();
}

export function updateDataFilter(data = {}) {
  return {
    type: Constants.UPDATE_DATA_FILTER,
    dataFilter: data
  };
}

export function updateAdvancedSliders(advancedSliders) {
  // updates advancedSlider values to save outside dataFilter for UI when
  // moving between 'Advanced' and 'Click/Open' radio buttons
  return {
    type: Constants.UPDATE_ADVANCED_SLIDERS,
    advancedSliders
  };
}

export function updateChartParams(data = {}) {
  return {
    type: Constants.UPDATE_CHART_PARAMS,
    chartParams: data
  };
}

export function requestFrequencies() {
  return {
    type: Constants.REQUEST_FREQUENCIES
  };
}

export function receiveFrequencies(json, error) {
  return {
    type: Constants.RECEIVE_FREQUENCIES,
    frequencies: json,
    error
  };
}

export function getFrequenciesData(options = {}, data = {}) {
  const filter = {
    filter: [
      'lookback',
      'emailType',
      'undersaturated',
      'oversaturated',
      'rangeStart',
      'rangeEnd'
    ]
  };
  const queryString = `?${qs.stringify(data, filter)}`;
  const path = `/api/v2/frequencies${queryString}`;
  const useMockData = options.useMockData === 'true';
  const useEinsteinDefaults = options.useEinsteinDefaults === 'true';
  return new Endpoint(path, requestFrequencies, receiveFrequencies)
    .withMock(
      useMockData || useEinsteinDefaults
        ? mockFrequencies(
            options.status,
            data.undersaturated,
            data.oversaturated,
            data.eventType
          )
        : null
    )
    .withOptions(options)
    .call();
}

export function requestUpdateDataExtension() {
  return {
    type: Constants.REQUEST_UPDATE_DATA_EXTENSION
  };
}

export function receiveUpdateDataExtension(json, error) {
  return {
    type: Constants.RECEIVE_UPDATE_DATA_EXTENSION,
    dataExtension: json,
    error
  };
}

export function submitUpdateDataExtension(options = {}, data) {
  const path = '/api/v2/data-extensions';
  return new Endpoint(
    path,
    requestUpdateDataExtension,
    receiveUpdateDataExtension
  )
    .withOptions(options)
    .withMethod('PUT')
    .withBody(JSON.stringify(data))
    .call();
}

export function requestCreateDataExtension() {
  return {
    type: Constants.REQUEST_CREATE_DATA_EXTENSION
  };
}

export function receiveCreateDataExtension(json, error) {
  return {
    type: Constants.RECEIVE_CREATE_DATA_EXTENSION,
    dataExtension: json,
    error
  };
}

export function submitCreateDataExtension(options = {}, data) {
  const path = '/api/v2/data-extensions';
  return new Endpoint(
    path,
    requestCreateDataExtension,
    receiveCreateDataExtension
  )
    .withOptions(options)
    .withMethod('PUT')
    .withBody(JSON.stringify(data))
    .call();
}

export function requestNotificationEmail() {
  return {
    type: Constants.REQUEST_NOTIFICATION_EMAIL
  };
}

export function receiveNotificationEmail(json, error) {
  return {
    type: Constants.RECEIVE_NOTIFICATION_EMAIL,
    response: json,
    error
  };
}

export function getNotificationEmail(options = {}) {
  const useMockData = options.useMockData === 'true';
  const path = '/api/v2/notification-subscribers';
  return new Endpoint(path, requestNotificationEmail, receiveNotificationEmail)
    .withMock(useMockData ? mockSubscribers() : null)
    .withOptions(options)
    .call();
}

export function requestUpdateNotificationEmail() {
  return {
    type: Constants.REQUEST_UPDATE_NOTIFICATION_EMAIL
  };
}

export function receiveUpdateNotificationEmail(json, error) {
  return {
    type: Constants.RECEIVE_UPDATE_NOTIFICATION_EMAIL,
    response: json,
    error
  };
}

export function submitUpdateNotificationEmail(options = {}, data) {
  const useMockActivation = options.useMockActivation === 'true';
  const path = '/api/v2/notification-subscribers';
  return new Endpoint(
    path,
    requestUpdateNotificationEmail,
    receiveUpdateNotificationEmail
  )
    .withMock(useMockActivation ? mockSubscribers(true) : null)
    .withOptions(options)
    .withMethod('PUT')
    .withBody(JSON.stringify(data))
    .call();
}

export function requestEEFV2ProvisionedStatus() {
  return {
    type: Constants.REQUEST_EEF_V2_PROVISIONED_STATUS
  };
}

export function receiveEEFV2ProvisionedStatus(json, error) {
  return {
    type: Constants.RECEIVE_EEF_V2_PROVISIONED_STATUS,
    response: json,
    error
  };
}

export function submitEEFV2ProvisionedStatus(options = {}) {
  let useMockData;
  if (options.hasOwnProperty('useMockEEFV2')) {
    useMockData = options['useMockEEFV2'] === 'true';
  }
  const path = '/api/v2/switch/227.eef.v2';
  return new Endpoint(
    path,
    requestEEFV2ProvisionedStatus,
    receiveEEFV2ProvisionedStatus
  )
    .withMock(useMockData)
    .withOptions(options)
    .call();
}
